import store from '@/store';

enum NovaAuthorizationCapability {
  PAYMENTS_OVERVIEW_READ = 'payments-overview.read',
  PROMOTIONS_ACCESS_READ = 'promotions-access.read',
  WALLET_PROVIDER_READ = 'wallet-provider.read',
}

type NovaPermissionAction = 'all' | 'create' | 'delete' | 'read' | 'update';
type NovaPlatformPermission = `${string}-${NovaPermissionAction}`;

const READ_IMPLYING_PERMISSION_ACTIONS = ['create', 'delete', 'update'] as const;

const NOVA_CAPABILITY_PERMISSIONS = {
  [NovaAuthorizationCapability.PAYMENTS_OVERVIEW_READ]: 'payments-overview-read',
  [NovaAuthorizationCapability.PROMOTIONS_ACCESS_READ]: 'promotions-access-read',
  [NovaAuthorizationCapability.WALLET_PROVIDER_READ]: 'wallet-provider-read',
} as const satisfies Record<NovaAuthorizationCapability, NovaPlatformPermission>;

function getRequiredPermission(capability: string): NovaPlatformPermission | undefined {
  if (!Object.prototype.hasOwnProperty.call(NOVA_CAPABILITY_PERMISSIONS, capability)) {
    return undefined;
  }

  const capabilityPermissions: Readonly<Record<string, NovaPlatformPermission>> = NOVA_CAPABILITY_PERMISSIONS;
  return capabilityPermissions[capability];
}

function hasRequiredPermission(
  permissions: ReadonlySet<string>,
  requiredPermission: NovaPlatformPermission,
): boolean {
  if (permissions.has(requiredPermission)) {
    return true;
  }

  const separatorIndex = requiredPermission.lastIndexOf('-');
  const action = requiredPermission.slice(separatorIndex + 1);
  const resource = requiredPermission.slice(0, separatorIndex);

  if (permissions.has(`${resource}-all`)) {
    return true;
  }

  return action === 'read'
    && READ_IMPLYING_PERMISSION_ACTIONS.some((impliedAction) => permissions.has(`${resource}-${impliedAction}`));
}

export function useNovaAuthorization(): {
  isAuthorized: (capability: string) => boolean;
} {
  function isAuthorized(capability: string): boolean {
    const requiredPermission = getRequiredPermission(capability);
    if (!requiredPermission) {
      return false;
    }

    const userPermissions = new Set<string>(store.state.userVaultnPermissions);
    return hasRequiredPermission(userPermissions, requiredPermission);
  }

  return { isAuthorized };
}

export { NOVA_CAPABILITY_PERMISSIONS, NovaAuthorizationCapability };
